import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandLogo } from '@/components/BrandLogo';
import { LanguageToggle } from '@/components/LanguageToggle';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { TEST_IDS } from '@/constants/testIds/app';

export default function Auth() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || (mode === 'signup' && !name)) {
      toast.error(t('auth.fillFields'));
      return;
    }
    setLoading(true);
    try {
      const path = mode === 'signup' ? '/auth/signup' : '/auth/login';
      const payload = mode === 'signup' ? { name, email, password } : { email, password };
      const res = await api.post(path, payload);
      setAuth(res.data.user);
      toast.success(mode === 'signup' ? t('auth.accountCreated') : t('auth.welcomeBack'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const startDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await api.post('/auth/demo');
      setAuth(res.data.user);
      toast.success(t('auth.demoReady'));
      navigate('/dashboard');
    } catch (e) {
      toast.error(t('auth.demoError'));
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="app-canvas relative flex min-h-screen flex-col">
      <div className="app-shell flex min-h-screen flex-col px-6 pb-10 pt-12">
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.06] text-foreground/80 hover:bg-white/[0.1]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <LanguageToggle />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-1">
            <BrandLogo size="lg" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight text-foreground">
            {mode === 'signup' ? t('auth.signupTitle') : t('auth.loginTitle')}
          </h1>
          <p className="mt-1 text-sm text-foreground/55">
            {mode === 'signup' ? t('auth.signupSubtitle') : t('auth.loginSubtitle')}
          </p>
        </motion.div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === 'signup' ? (
            <div className="space-y-1.5">
              <Label className="text-xs text-foreground/70">{t('auth.name')}</Label>
              <Input
                data-testid={TEST_IDS.auth.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth.namePlaceholder')}
                className="h-11 rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40"
              />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label className="text-xs text-foreground/70">{t('auth.email')}</Label>
            <Input
              data-testid={TEST_IDS.auth.emailInput}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              className="h-11 rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-foreground/70">{t('auth.password')}</Label>
            <Input
              data-testid={TEST_IDS.auth.passwordInput}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-11 rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40"
            />
          </div>

          <Button
            data-testid={TEST_IDS.auth.submit}
            type="submit"
            disabled={loading}
            className="btn-brand h-12 w-full rounded-xl text-base font-semibold text-brand-ink"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {mode === 'signup' ? t('auth.createAccount') : t('auth.login')}
          </Button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-foreground/40">
          <div className="h-px flex-1 bg-white/10" />
          {t('auth.or')}
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <Button
          data-testid={TEST_IDS.auth.demo}
          onClick={startDemo}
          disabled={demoLoading}
          variant="secondary"
          className="h-12 w-full rounded-xl border border-brand-blue/25 bg-white/[0.06] text-base font-medium text-foreground hover:border-brand-cyan/40 hover:bg-white/[0.1]"
        >
          {demoLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t('auth.demo')}
        </Button>

        <p className="mt-6 text-center text-sm text-foreground/55">
          {mode === 'signup' ? t('auth.haveAccount') : t('auth.noAccount')}{' '}
          <button
            data-testid={TEST_IDS.auth.toggle}
            onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
            className="font-semibold text-brand-cyan hover:underline"
          >
            {mode === 'signup' ? t('auth.login') : t('auth.signup')}
          </button>
        </p>
      </div>
    </div>
  );
}
